// src/pages/ProjectsPage.jsx
import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Projects from "../components/Projects";

import { useEffect, useState } from "react";

import AnimatedPage from "../components/AnimatedPage";

import { getProjects } from "../components/api";

import { useNavigate, useLocation } from "react-router-dom";

const ProjectsPage = ({ toggleDarkMode, isDarkMode, pageAnimated }) => {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    async function fetchProjects() {
      try {
        const data = await getProjects();
        setProjects(data.data); // Strapi 返回的是 { data: [...] }
      } catch (err) {
        console.error("Failed to load projects", err);
      }
    }

    fetchProjects();
  }, []);

  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    if (selectedProject) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
    };
  }, [selectedProject]);

  const navigate = useNavigate();

  const location = useLocation();
  // 新增：根据路由状态自动打开对应项目
  useEffect(() => {
    if (location.state?.projectId && projects.length > 0) {
      const targetProject = projects.find(
        (p) => p.id === location.state.projectId
      );
      if (targetProject) {
        setSelectedProject(targetProject);
      }
    }
  }, [location.state, projects]);

  return (
    <>
      <AnimatedPage>
        <Header toggleDarkMode={toggleDarkMode} isDarkMode={isDarkMode} />
        <main className="min-h-screen py-16 bg-orange-100 dark:bg-black text-black dark:text-white">
          <div className="container mx-auto px-4">
            <button className="cursor-pointer" onClick={() => navigate(-1)}>
              <div className="text-4xl font-bold mb-8 mt-[40px] flex items-center gap-3 ">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  class="w-[40px] h-[40px]"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3"
                  />
                </svg>
                <p className="text-[40px]">AII Projects</p>
              </div>
            </button>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-[40px]">
              {projects.map((project) => {
                const { id, hometitle, cover, detail, date, summary } = project;
                const imageUrl = cover?.formats?.small?.url || cover?.url || "";
                const description = detail?.[0]?.children?.[0]?.text || "";

                return (
                  <div
                    key={id}
                    onClick={() => setSelectedProject(project)}
                    className="group relative overflow-hidden rounded-lg shadow-md bg-white/10 dark:shadow-[0_2px_8px_rgba(255,255,255,.25)] cursor-pointer"
                  >
                    {/* ✅ 背景遮罩，hover 出现 */}
                    <div className="absolute inset-0 bg-black/0 group-hover:backdrop-blur-xl group-hover:bg-black/60 transition-all duration-300 z-30" />

                    {/* ✅ 图片放大 */}
                    <img
                      src={imageUrl}
                      alt={`${hometitle} cover`}
                      className="w-full h-[250px] object-cover transition-all duration-500 group-hover:scale-150"
                    />

                    {/* ✅ 文本浮层，默认静止，hover 后浮到中间 */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-40 transition-all duration-500">
                      <h2 className="text-xl font-semibold text-white transition-all duration-500 hidden group-hover:block group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 ">
                        {hometitle}
                      </h2>
                      <p className="text-sm text-white mt-2 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                        {description}
                      </p>
                      <p className="text-xs text-gray-300 mt-1 opacity-0 group-hover:opacity-100 translate-y-6 group-hover:translate-y-0 transition-all duration-700">
                        {date}
                      </p>
                    </div>

                    {/* ✅ 默认展示的文字（hover 时隐藏） */}
                    <div className="p-4 z-10 group-hover:opacity-0 transition-opacity duration-300 relative">
                      <h2 className="text-xl font-semibold mb-1">
                        {hometitle}
                      </h2>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                        {summary}
                      </p>

                      {/* ✅ 日期固定在右下角 */}
                      <div className="absolute top-5 right-4 text-xs text-gray-700 dark:text-gray-200">
                        {date}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* ✅ 项目详情弹窗 */}
              {selectedProject && (
                <div
                  className="fixed inset-0 bg-white/70 dark:bg-black/70 backdrop-blur-xl z-50 overflow-y-auto scrollbar-none"
                  onClick={() => setSelectedProject(null)}
                >
                  <div
                    className="min-h-screen w-full max-w-4xl mx-auto px-6 py-20 relative text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="absolute top-6 right-6 text-[35px] lg:top-3 lg:right-0 lg:text-[70px] text-gray-600 dark:text-gray-300 opacity-50
                      hover:opacity-100 transition-opacity duration-300"
                      onClick={() => setSelectedProject(null)}
                    >
                      ×
                    </button>

                    <h2 className="text-[40px] lg:text-[80px] font-bold mb-6">
                      {selectedProject.hometitle}
                    </h2>

                    {selectedProject.cover && (
                      <img
                        src={
                          selectedProject.cover.formats?.medium?.url ||
                          selectedProject.cover.url ||
                          ""
                        }
                        alt="Project Cover"
                        className="mx-auto rounded max-h-[350px] lg:max-h-[450px] w-full object-contain mb-6"
                      />
                    )}

                    {selectedProject.detail?.map((para, index) => (
                      <p
                        key={index}
                        className="text-left text-[20px] lg:text-[35px] text-gray-800 dark:text-gray-200 mb-4 leading-relaxed"
                      >
                        {para.children?.map((child) => child.text).join("")}
                      </p>
                    ))}

                    <p className="text-[20px] text-black dark:text-gray-200 mt-4">
                      {selectedProject.date}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer toggleDarkMode={toggleDarkMode} isDarkMode={isDarkMode} />
      </AnimatedPage>
    </>
  );
};

export default ProjectsPage;
